import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { captureUtm, getUtm } from "../lib/utm";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CheckCircle, Phone, ArrowRight, ShieldCheck, CaretDown } from "@phosphor-icons/react";

const PHONE = "800-719-6912";
const PHONE_TEL = "tel:8007196912";

const NOISE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`;
const Noise = () => <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: NOISE }} />;

const FAQS = [
  ["So what's the hourly rate?", "There's no single statewide rate. Through Community HealthChoices, most family caregivers in Pennsylvania earn about $15–$19 an hour — and in higher-cost counties rates can reach roughly $20–$22. Your exact rate depends on your county, your loved one's plan, and the hours the program approves."],
  ["Who decides the rate and the hours?", "The program approves a care plan with a set number of weekly hours. In the participant-directed option (Services My Way), the person receiving care and the caregiver agree on the hourly wage within the program's county and plan limits."],
  ["Didn't rates just go up?", "Yes — Pennsylvania's 2025–26 budget raised direct-care wages, with increases of $1 to $5 an hour for some workers, retroactive to January 1, 2026. The fiscal intermediaries that run payroll publish the current caps — Tempus Unlimited has a 2026 pay-rate calculator for PA."],
  ["Is the pay taxed?", "It can be tax-free. If you live in the same home as the person you care for, IRS Notice 2014-7 may let you exclude qualifying Medicaid waiver payments from federal income tax — related or not. Confirm your situation with a tax professional."],
  ["How do I find out my exact number?", "That's what the free review is for. We look at your county and your situation and give you real figures for what family caregivers are earning — and what to ask for."],
];

export default function PayRatesPage() {
  const [lead, setLead] = useState({ first: "", last: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const leadValid = lead.first.trim() && lead.last.trim() && (lead.phone.trim() || /.+@.+\..+/.test(lead.email));

  useEffect(() => {
    captureUtm();
    document.title = "How Much Do Family Caregivers Get Paid in Pennsylvania? (2026) | Silver Home Care";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = "Most PA family caregivers earn about $15–$19 an hour through Community HealthChoices — up to $20–$22 in higher-cost counties, and possibly tax-free if you live together. See what decides your rate and get your real number free.";
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "faq-schema";
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    document.head.appendChild(ld);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, []); // eslint-disable-next-line

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/care-inquiry", { name: `${lead.first.trim()} ${lead.last.trim()}`, phone: lead.phone, email: lead.email, answers: {}, utm: getUtm(), source: "paid-caregiver-pay-rates" });
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const toForm = () => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="min-h-screen bg-white text-[#23282f]" data-testid="pay-rates-page">
      <style>{`
        @keyframes fcRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        .fc-rise { animation: fcRise .7s cubic-bezier(.22,.68,.36,1) both; }
        .fc-rise-1 { animation-delay: .1s; } .fc-rise-2 { animation-delay: .2s; }
      `}</style>

      <header className="sticky top-0 z-50 border-b border-[#23282f]/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
            <p className="font-heading text-lg font-semibold tracking-tight">Silver Home Care</p>
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-stone-600 md:flex" data-testid="page-nav">
            <a href="/#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="/#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="/reviews" className="transition-colors hover:text-[#6A3E97]">Reviews</a>
            <a href="/free-care" className="font-semibold text-[#6A3E97] transition-colors hover:text-[#55317A]">Free services</a>
            <a href="/apply" className="transition-colors hover:text-[#6A3E97]">Apply</a>
            <a href="/#contact" className="transition-colors hover:text-[#6A3E97]">Contact</a>
          </nav>
          <a href={PHONE_TEL}
            className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#4A235A] focus:ring-2 focus:ring-[#6A3E97] focus:ring-offset-2"
            data-testid="header-phone">
            <Phone size={16} weight="fill" /> {PHONE}
          </a>
        </div>
      </header>

      {/* HERO — plum, with the quick-answer card */}
      <section className="relative overflow-hidden bg-[#4A235A] text-white">
        <Noise />
        <div className="relative mx-auto max-w-4xl px-5 pb-16 pt-14 sm:px-8 lg:pb-20 lg:pt-20">
          <p className="fc-rise text-xs font-bold uppercase tracking-[0.2em] text-[#C9A6E8] sm:text-sm">Pennsylvania · Community HealthChoices · 2026</p>
          <h1 className="fc-rise fc-rise-1 mt-5 font-heading text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl" data-testid="hero-headline">
            How much do family caregivers get paid in Pennsylvania?
          </h1>
          <div className="fc-rise fc-rise-2 mt-10 rounded-2xl bg-white p-7 text-[#23282f] shadow-2xl shadow-black/30 sm:p-10" data-testid="quick-answer">
            <p className="font-heading text-2xl font-bold leading-snug tracking-tight sm:text-4xl">
              Most earn about <span className="text-[#6A3E97]">$15–$19 an hour</span> — up to <span className="text-[#6A3E97]">$20–$22</span> in higher-cost counties.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#23282f]/75 sm:text-lg">
              And if you live in the same home as the person you care for, that pay can be
              <strong className="font-bold text-[#23282f]"> excluded from federal income tax</strong> under IRS Notice 2014-7.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-[#23282f]/55">
              Example: 20 approved hours a week at $16/hour is about $320 a week — for care many families are already giving for free.
            </p>
          </div>
        </div>
      </section>

      {/* FACTORS */}
      <section className="px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6A3E97]">The fine print that matters</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="factors-heading">What decides your exact number</h2>
          <div className="mt-10 space-y-5">
            {[
              { t: "Your county and their plan", d: "Rates are capped by county and by the CHC plan (Keystone First, PA Health & Wellness, UPMC, AmeriHealth Caritas). Philadelphia-area caps tend to run higher than rural counties." },
              { t: "The approved hours", d: "The program assesses your loved one and approves a set number of weekly hours. More need = more approved hours = more pay for the same work you're already doing." },
              { t: "The wage agreement", d: "In the participant-directed option (Services My Way), the person receiving care and the caregiver agree on the hourly wage within the program limits." },
              { t: "The 2026 wage increases", d: "PA's 2025–26 budget raised direct-care wages $1–$5 an hour for some workers, retroactive to January 1, 2026. Tempus Unlimited (a payroll intermediary) publishes a current pay-rate calculator for PA." },
            ].map((f, i) => (
              <div key={f.t} className="flex items-start gap-5 rounded-2xl border border-[#23282f]/10 bg-white p-6 shadow-[2px_2px_10px_rgba(0,0,0,0.05)]" data-testid={`factor-${i}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#6A3E97] font-heading text-lg font-bold text-white">{i + 1}</span>
                <div>
                  <p className="font-heading text-xl font-semibold tracking-tight">{f.t}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#23282f]/65">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAX CALLOUT — dark */}
      <section className="relative overflow-hidden bg-[#23282f] px-5 py-16 text-white sm:px-8 lg:py-20">
        <Noise />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="tax-callout">Live together? It may all be tax-free.</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Caregivers who share a home with the person they care for may exclude this pay from federal income tax entirely.
            That's on top of the rate — and most families have never heard of it.
          </p>
          <Link to="/get-paid-to-care-for-a-loved-one-in-pennsylvania"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-[#23282f] transition-all duration-200 hover:-translate-y-1 hover:bg-[#C9A6E8]"
            data-testid="tax-callout-link">
            How the whole program works <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="bg-[#C9A6E8]/15 px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-xl">
          <div id="lead-form" className="rounded-2xl bg-white p-6 shadow-2xl shadow-black/10 sm:p-10" data-testid="lead-card">
            {sent ? (
              <div className="text-center" data-testid="inquiry-success">
                <CheckCircle size={56} weight="fill" className="mx-auto text-[#6A3E97]" />
                <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight">You're all set{lead.first ? `, ${lead.first}` : ""}.</h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#23282f]/70 sm:text-base">
                  A care coordinator will call with real numbers for your situation — usually the same or next business day.
                  Want to talk now? <a href={PHONE_TEL} className="font-bold text-[#6A3E97]">{PHONE}</a>
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="lead-headline">Get your real number — free</h2>
                <p className="mt-2 text-sm leading-relaxed text-[#23282f]/60">Your county and your situation — reviewed by a real person, not a computer.</p>
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input value={lead.first} onChange={(e) => setLead((l) => ({ ...l, first: e.target.value }))} placeholder="First name *"
                      className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-name" />
                    <Input value={lead.last} onChange={(e) => setLead((l) => ({ ...l, last: e.target.value }))} placeholder="Last name *"
                      className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-last-name" />
                  </div>
                  <Input value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))} placeholder="Mobile phone number"
                    type="tel" className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-phone" />
                  <Input type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} placeholder="Email address"
                    className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-email" />
                  <Button onClick={submit} disabled={busy || !leadValid} size="lg"
                    className="w-full rounded-full bg-[#6A3E97] py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#4A235A] active:translate-y-0"
                    data-testid="lead-submit">
                    {busy ? "Sending…" : "Get my real number"} <ArrowRight size={18} weight="bold" className="ml-1" />
                  </Button>
                  <p className="flex items-center justify-center gap-1.5 text-center text-xs text-[#23282f]/45">
                    <ShieldCheck size={13} /> Private & secure · a real person responds within one business day
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="faq-heading">Common questions</h2>
          <div className="mt-10 divide-y divide-[#23282f]/10 border-y border-[#23282f]/10">
            {FAQS.map(([q, a], i) => (
              <div key={q}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between py-5 text-left font-heading text-lg font-semibold text-[#23282f] transition-colors hover:text-[#6A3E97] focus:ring-2 focus:ring-[#6A3E97]"
                  data-testid={`faq-q-${i}`}>
                  {q}
                  <CaretDown size={18} className={`shrink-0 text-[#23282f]/40 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-6 text-sm leading-relaxed text-[#23282f]/70 sm:text-base" data-testid={`faq-a-${i}`}>{a}</p>}
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs font-bold uppercase tracking-[0.15em] text-[#23282f]/40" data-testid="trust-strip">
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#6A3E97]" /> Licensed PA agency</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#6A3E97]" /> Ten years serving Bucks & Philadelphia</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#6A3E97]" /> Free review</span>
          </div>
        </div>
      </section>

      <footer className="bg-[#23282f] py-8 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 text-xs text-white/50 sm:px-8">
          <p>© {new Date().getFullYear()} Silver Home Care · Licensed Pennsylvania non-medical home care agency · {PHONE}</p>
          <div className="flex items-center gap-5">
            <a href="/" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-main-site">Main site</a>
            <Link to="/get-paid-to-care-for-a-loved-one-in-pennsylvania" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-paid-caregiver">Get paid to care for family</Link>
            <a href="/free-care" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-free-care">Free care through CHC</a>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      {!sent && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-[#23282f]/10 bg-white/95 p-4 backdrop-blur-md lg:hidden" data-testid="sticky-mobile-cta">
          <div className="flex gap-2">
            <button onClick={toForm} className="flex-1 rounded-full bg-[#6A3E97] py-3.5 text-sm font-bold text-white shadow-md" data-testid="sticky-cta-btn">
              Get my real number
            </button>
            <a href={PHONE_TEL} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border-2 border-[#6A3E97] py-3.5 text-sm font-bold text-[#6A3E97]" data-testid="sticky-call-btn">
              <Phone size={15} weight="fill" /> Call now
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
